import { addonKeyToIds } from './addon/index.ts';
import {
    AdditionTermsPageEventQuery,
    AddonVariationUnitPrice,
    ConfirmationPageEventQuery,
    DiscountType,
    Maybe,
    OrderSummaryEventQuery,
    PaymentPageEventQuery,
    QuestionnairePageEventQuery,
    TicketCategoryUnitPrice,
} from '../graphql/graphql.ts';
import { A1CheckoutCartObject } from '../types/common.ts';
import sha256 from 'crypto-js/sha256';

export enum LineItemType {
    TICKET_LINE_ITEM,
    ADDON_LINE_ITEM,
}

export enum FeeID {
    BASE_PRICE = '000',
}

export interface FeeItem {
    id: string;
    name: string;
    subTotal: number;
    order?: number;
}

export interface LineItem extends FeeItem {
    type: LineItemType;
    quantity: number;
    unitPrice: Array<Maybe<AddonVariationUnitPrice> | Maybe<TicketCategoryUnitPrice>>;
    originalSubTotal?: number;
    discount?: number;
    promoCode?: string;
}

export function convertCartToLineItems(
    cart: A1CheckoutCartObject,
    event:
        | OrderSummaryEventQuery['event']
        | PaymentPageEventQuery['event']
        | ConfirmationPageEventQuery['event']
        | QuestionnairePageEventQuery['event']
        | AdditionTermsPageEventQuery['event']
) {
    const items: LineItem[] = [];

    // Get active promo codes and their configurations
    const activePromoCodes = cart.promoCodes || [];
    const promoCodeConfigs = activePromoCodes
        .map((code) => {
            const hashedCode = sha256(code.toUpperCase().trim()).toString();
            return event?.checkoutPromoCodes?.find((pc) => pc?.hashedPromoCode === hashedCode);
        })
        .filter(Boolean);

    const promoApplicationTracker = new Map<string, number>();

    // Process ticket quantities
    for (const [categoryId, qty] of Object.entries(cart.quantities)) {
        if (qty <= 0) {
            continue;
        }

        const category = event?.ticketCategories?.find((cat) => cat?.categoryId === categoryId);

        if (!category) {
            continue;
        }

        const basePrice = category?.unitPrice?.find((u) => u?.id === FeeID.BASE_PRICE);
        const originalAmount = basePrice?.charge?.amount ?? 0;
        let discountedAmount = originalAmount;
        let appliedPromoCode: string | undefined;
        let discountAmount = 0;
        let discountableQty = qty;

        // Check if any promo code applies to this ticket category
        for (const promoConfig of promoCodeConfigs) {
            if (!promoConfig) continue;

            // Check if promo applies to this category
            const appliesToCategory =
                !promoConfig.ticketTypes?.length || promoConfig.ticketTypes.includes(categoryId);

            if (appliesToCategory) {
                // Check date validity
                const now = new Date();
                if (promoConfig.startDate && new Date(promoConfig.startDate) > now) continue;
                if (promoConfig.endDate && new Date(promoConfig.endDate) < now) continue;

                // Count total eligible tickets for this promo across ALL categories
                let totalEligibleTickets = 0;
                for (const [catId, catQty] of Object.entries(cart.quantities)) {
                    if (
                        !promoConfig.ticketTypes?.length ||
                        promoConfig.ticketTypes.includes(catId)
                    ) {
                        totalEligibleTickets += catQty;
                    }
                }

                // Check minimum ticket requirement
                const minRequired = promoConfig.minTicketsPerOrder || 0;
                if (totalEligibleTickets < minRequired) {
                    continue;
                }

                // Handle maximum tickets limit
                const maxAllowed = promoConfig.maxTicketsPerOrder || Number.MAX_SAFE_INTEGER;
                const promoIndex = promoCodeConfigs.indexOf(promoConfig);
                const promoCodeString = activePromoCodes[promoIndex];

                // Get how many tickets already have this discount (if processing multiple categories)
                const alreadyDiscounted = promoApplicationTracker.get(promoCodeString) || 0;
                const remainingDiscountable = Math.max(0, maxAllowed - alreadyDiscounted);
                discountableQty = Math.min(qty, remainingDiscountable);

                if (discountableQty === 0) {
                    // Max tickets already discounted
                    continue;
                }

                // Update tracker
                promoApplicationTracker.set(promoCodeString, alreadyDiscounted + discountableQty);

                // Apply discount
                if (
                    promoConfig.discountType === DiscountType.Percentage &&
                    promoConfig.discountAmount
                ) {
                    discountAmount = Math.round(
                        originalAmount * (promoConfig.discountAmount / 100)
                    );
                    discountedAmount = originalAmount - discountAmount;
                } else if (
                    promoConfig.discountType === DiscountType.Flat &&
                    promoConfig.discountAmount
                ) {
                    discountAmount = Math.round(
                        Math.min(promoConfig.discountAmount, originalAmount)
                    );
                    discountedAmount = originalAmount - discountAmount;
                }

                appliedPromoCode = activePromoCodes[promoIndex];
                break;
            }
        }

        // Create a modified unitPrice array with the discounted base price
        const modifiedUnitPrice = category.unitPrice?.map((price) => {
            if (price?.id === FeeID.BASE_PRICE && price?.charge) {
                // Create a properly typed copy
                const modifiedPrice: TicketCategoryUnitPrice = {
                    __typename: 'TicketCategoryUnitPrice',
                    id: price.id,
                    name: price.name,
                    charge: {
                        __typename: 'Money',
                        amount: Math.round(discountedAmount), // Ensure integer
                        currency: price.charge.currency,
                    },
                    order: 0,
                };
                return modifiedPrice;
            }
            return price;
        }) as Array<Maybe<TicketCategoryUnitPrice>>;

        const lineItemSubTotal =
            appliedPromoCode && discountableQty
                ? Math.round(
                      discountedAmount * discountableQty + originalAmount * (qty - discountableQty)
                  )
                : Math.round(originalAmount * qty);

        const lineItem: LineItem = {
            type: LineItemType.TICKET_LINE_ITEM,
            id: categoryId,
            name: category!.name!,
            quantity: qty,
            unitPrice: modifiedUnitPrice || category!.unitPrice!,
            subTotal: lineItemSubTotal,
            originalSubTotal: appliedPromoCode ? Math.round(originalAmount * qty) : undefined,
            discount: appliedPromoCode ? Math.round(discountAmount * discountableQty) : undefined,
            promoCode: appliedPromoCode,
        };

        items.push(lineItem);
    }

    // Process addon quantities (no promo codes based on your schema)
    for (const [key, qty] of Object.entries(cart.addons)) {
        if (qty <= 0) {
            continue;
        }

        const [categoryId, variationId] = addonKeyToIds(key);

        const category = event?.addonCategories?.find((cat) => cat?.categoryId === categoryId);

        if (!category) {
            continue;
        }

        const variation = category?.variations?.find(
            (variation) => variation.variationId === variationId
        );

        if (!variation) {
            continue;
        }

        const isSingleVariation = category?.variations?.length === 1;
        const basePrice = variation?.unitPrice?.find((u) => u?.id === FeeID.BASE_PRICE);

        items.push({
            type: LineItemType.ADDON_LINE_ITEM,
            id: key,
            name: isSingleVariation ? category!.name : `${category.name} / ${variation.name}`,
            quantity: isNaN(qty) ? 0 : qty,
            unitPrice: variation!.unitPrice!,
            subTotal: isNaN(qty) ? 0 : (basePrice?.charge?.amount ?? 0) * qty,
        });
    }

    // Process flexible pricing
    for (const [flexibleKey, flexibleOpt] of Object.entries(cart.flexiblePricing)) {
        if (flexibleOpt.amount <= 0) {
            continue;
        }

        const category = event?.ticketCategories?.find((cat) => cat?.categoryId === flexibleKey);

        if (!category) {
            continue;
        }

        const basePrice = flexibleOpt?.fees?.find((u) => u?.id === FeeID.BASE_PRICE);

        items.push({
            type: LineItemType.TICKET_LINE_ITEM,
            id: flexibleKey,
            name: category!.name!,
            quantity: 1,
            unitPrice: flexibleOpt!.fees!,
            subTotal: (basePrice?.charge?.amount ?? 0) * 1,
        });
    }

    return items;
}

export function extractFeeItemsFromLineItems(lineItems: LineItem[]) {
    const fees: FeeItem[] = [];

    if (!Array.isArray(lineItems)) {
        return fees;
    }

    for (const lineItem of lineItems) {
        if (!Array.isArray(lineItem?.unitPrice)) {
            continue;
        }

        for (const unitPrice of lineItem.unitPrice) {
            if (!unitPrice?.id || unitPrice?.id === FeeID.BASE_PRICE) {
                continue;
            }

            const subTotal = (unitPrice?.charge?.amount ?? 0) * lineItem.quantity;
            const fee = fees.find((f) => f.id === unitPrice.id);
            const order = unitPrice.order;

            if (fee) {
                fee.subTotal += subTotal;
            } else {
                fees.push({
                    id: unitPrice.id,
                    name: unitPrice.name!,
                    subTotal,
                    order: order ?? 0,
                });
            }
        }
    }

    return fees;
}

export function calculateSubtotal(lineItems: LineItem[]) {
    return lineItems.reduce((accumulator, currentValue) => {
        return accumulator + currentValue.subTotal;
    }, 0);
}

export function calculateTotal(lineItems: LineItem[], feeItems: FeeItem[]) {
    return [...lineItems, ...feeItems].reduce((total, item) => total + item.subTotal, 0);
}

export function calculateTotalDiscount(lineItems: LineItem[]) {
    return lineItems.reduce((total, item) => total + (item.discount || 0), 0);
}

export function removeZeroQuantities(quantities: Record<string, number>) {
    return Object.fromEntries(Object.entries(quantities).filter(([, qty]) => qty > 0));
}
